<template>
  <div v-loading="$wait.waiting(_uid)" class="card">
    <div class="card-content">
      <div class="text-right control-button-wrapper pt-xxs">
        <acl :roles="canDeleteEvent()">
          <button
            type="button"
            class="btn btn-danger btn-wd submit-button mr-5"
            @click="handleDeleteEvent"
          >
            Delete
          </button>
        </acl>
        <router-link :to="{ name: 'my_events@show', params: { id: event.id } }">
          <button type="button" class="btn btn-primary btn-wd mr-5">
            Cancel
          </button>
        </router-link>

        <acl :roles="canActivateEvent()">
          <button
            v-show="!event.is_active"
            type="button"
            class="btn btn-success btn-wd mr-5"
            :class="{ loading: $wait.waiting(_uid) }"
            @click="handleActivateEvent"
          >
            Restore
            <i
              v-show="$wait.waiting(_uid)"
              class="fas fa-circle-notch loading-icon"
            />
          </button>
        </acl>
        <acl :roles="canDeactivateEvent()">
          <button
            v-show="event.is_active"
            type="button"
            class="btn btn-danger btn-wd mr-5"
            :class="{ loading: $wait.waiting(_uid) }"
            @click="handleDeactivateEvent"
          >
            Archive
            <i
              v-show="$wait.waiting(_uid)"
              class="fas fa-circle-notch loading-icon"
            />
          </button>
        </acl>

        <acl :roles="canUpdateEvent()">
          <button
            v-show="event.is_active"
            type="submit"
            class="btn btn-info btn-fill btn-wd submit-button"
            :class="{ loading: $wait.waiting(_uid) }"
            @click="handleUpdate"
          >
            Update
            <i
              v-show="$wait.waiting(_uid)"
              class="fas fa-circle-notch loading-icon"
            />
          </button>
        </acl>
      </div>
      <EventForm :event="event" @eventDeleteFile="handleDeleteFile" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import MyService from "@app/services/my";
import swal from "sweetalert2";
import EventForm from "@components/Events/Components/Form.vue";
import _cloneDeep from "lodash.clonedeep";
import rolesConstants from "@/components/acl/roles";
import { getStorageData } from "@/utils/storage";
import parseSettings from "@providers/parseSettings";

export default {
  components: {
    EventForm,
  },
  data() {
    return {
      event: {
        description: null,
        notes: null,
        issued_at: null,
        expiry_at: null,
        attachments: [],
        files: [],
        manager: null,
        trainee: null,
        reminders: [
          { key: "days", value: "0" },
          { key: "days", value: "0" },
          { key: "days", value: "0" },
        ],
      },
    };
  },
  computed: {
    ...mapGetters("loadingStore", { isLoading: "loading" }),
    routeParams() {
      return this.$route.params;
    },
  },
  created() {
    this.ROLE_SUPER = rolesConstants.ROLE_SUPER;
    this.ROLE_ADMIN = rolesConstants.ROLE_ADMIN;
    this.ROLE_COMPANY_MANAGER = rolesConstants.ROLE_COMPANY_MANAGER;
    this.ROLE_TRAINING_MANAGER = rolesConstants.ROLE_TRAINING_MANAGER;
    this.ROLE_TRAINEE = rolesConstants.ROLE_TRAINEE;
    this.ROLE_SUPPORT = rolesConstants.ROLE_SUPPORT;
    this.ROLE_CTOPS = rolesConstants.ROLE_CTOPS;
  },
  async mounted() {
    try {
      this.$wait.start(this._uid);

      const event = await MyService.showEvent(this.routeParams.id);

      event.attachments = event.files;
      event.files = [];

      if (!event.reminders.length) {
        event.reminders = this.event.reminders;
      }

      const reminderCount = event.reminders.length;
      const defaultReminderCount = this.event.reminders.length;
      const remaningCount = defaultReminderCount - reminderCount;

      if (reminderCount > 0) {
        const toAdd = this.event.reminders.splice(0, remaningCount);
        event.reminders = [...event.reminders, ...toAdd];
      }

      this.event = event;
    } catch (error) {
      this.addNewError(error);
    } finally {
      this.$wait.end(this._uid);
    }
  },
  methods: {
    ...mapActions("loadingStore", ["setLoading"]),
    ...mapActions("errorStore", ["addNewError"]),
    parseSettings,
    canDeleteEvent() {
      const roleList = [];

      roleList.push(this.ROLE_COMPANY_MANAGER);
      roleList.push(this.ROLE_TRAINING_MANAGER);

      const company = getStorageData("company");
      const companySettings = this.parseSettings(company.settings);

      if (companySettings.events_documents) {
        roleList.push(this.ROLE_TRAINEE);
        return roleList;
      }

      return roleList;
    },
    canActivateEvent() {
      const roleList = [];

      roleList.push(this.ROLE_COMPANY_MANAGER);
      roleList.push(this.ROLE_TRAINING_MANAGER);
      
      return roleList;
    },
    canDeactivateEvent() {
      const roleList = [];

      roleList.push(this.ROLE_COMPANY_MANAGER);
      roleList.push(this.ROLE_TRAINING_MANAGER);
      
      return roleList;
    },
    canUpdateEvent() {
      const roleList = [];

      roleList.push(this.ROLE_COMPANY_MANAGER);
      roleList.push(this.ROLE_TRAINING_MANAGER);

      const company = getStorageData("company");
      const companySettings = this.parseSettings(company.settings);

      if (companySettings.events_documents) {
        roleList.push(this.ROLE_TRAINEE);
        return roleList;
      }

      return roleList;
    },
    async handleUpdate() {
      try {
        this.$wait.start(this._uid);

        let event = _cloneDeep(this.event);

        delete event.attachments;

        event = await MyService.updateEvent(event.id, event);

        this.$notify({
          message: "Data updated successfully!",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "success",
        });

        this.$router.push({
          name: "my_events@show",
          params: { id: event.id },
        });
      } catch (error) {
        this.addNewError(error);
      } finally {
        this.$wait.end(this._uid);
      }
    },
    async handleDeleteEvent() {
      try {
        this.$wait.start(this._uid);

        const shouldDelete = await swal({
          title: "Confirmation",
          text: "Do you really want to delete this event?",
          type: "warning",
          buttonsStyling: false,
          reverseButtons: true,
          confirmButtonClass: "btn ml-xs btn-success btn-fill",
          cancelButtonClass: "btn btn-danger btn-fill",
          showCancelButton: true,
        });

        if (!shouldDelete) {
          return;
        }

        await MyService.deleteEvent(this.event.id);

        this.$notify({
          message: "Event deleted successfully!",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "success",
        });

        this.$router.push({ name: "my_events@index" });
      } catch (error) {
        this.addNewError(error);
      } finally {
        this.$wait.end(this._uid);
      }
    },
    async handleDeleteFile({ event, file }) {
      try {
        this.$wait.start(this._uid);

        const shouldDelete = await swal({
          title: "Confirmation",
          text: "Do you really want to delete this file?",
          type: "warning",
          buttonsStyling: false,
          reverseButtons: true,
          confirmButtonClass: "btn ml-xs btn-success btn-fill",
          cancelButtonClass: "btn btn-danger btn-fill",
          showCancelButton: true,
        });

        if (!shouldDelete) {
          return;
        }

        await MyService.deleteFile(event.id, file.id);

        //SHOW ACTION
        const attachmentList = this.event.attachments;
        const index = attachmentList.findIndex(
          ({ id: cardId }) => file.id === cardId
        );
        attachmentList.splice(index, 1);
      } catch (error) {
        this.addNewError(error);
      } finally {
        this.$wait.end(this._uid);
      }
    },
    async handleActivateEvent() {
      try {
        this.$wait.start(this._uid);

        const event = await MyService.activateEvent(this.event.id);

        this.$notify({
          message: "Event restored successfully!",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "success",
        });

        this.event = event;

        // this.$router.push({
        //   name: "my_events@show",
        //   params: { id: event.id },
        // });
      } catch (error) {
        this.addNewError(error);
      } finally {
        this.$wait.end(this._uid);
      }
    },
    async handleDeactivateEvent() {
      try {
        this.$wait.start(this._uid);

        const event = await MyService.deactivateEvent(this.event.id);

        this.$notify({
          message: "Event archived successfully!",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "success",
        });

        this.event = event;

        // this.$router.push({
        //   name: "my_events@show",
        //   params: { id: event.id },
        // });
      } catch (error) {
        this.addNewError(error);
      } finally {
        this.$wait.end(this._uid);
      }
    },
  },
};
</script>

<style lang="scss"></style>
